import React, { useState } from "react";
import { createPortal } from "react-dom";
import { History, Calendar, Check, X } from "lucide-react";
import { FIELD_LABELS, formatFieldValue } from "@/lib/empleadoVersioning";

/**
 * Diálogo que aparece al guardar un empleado cuando hay cambios en campos versionados.
 * Permite al usuario fijar una fecha efectiva por cada campo. Default: hoy.
 *
 * Props:
 *   diffs: [{ field, old_value, new_value }]
 *   defaultDate: "YYYY-MM-DD" (sugerido)
 *   onConfirm: (array of { field, old_value, new_value, effective_from }) => void
 *   onCancel: () => void
 */
export default function CambioEfectivoDialog({ diffs, defaultDate, onConfirm, onCancel }) {
  const today = defaultDate || new Date().toISOString().slice(0, 10);
  const [fechas, setFechas] = useState(() => Object.fromEntries(diffs.map(d => [d.field, today])));
  const confirm = () => {
    const payload = diffs.map(d => ({
      ...d,
      effective_from: fechas[d.field] || today,
    }));
    onConfirm(payload);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
      data-testid="cambio-efectivo-dialog"
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onCancel(); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-c">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
              <History size={18} />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold">Confirmar fecha efectiva del cambio</h2>
              <p className="text-xs text-secondary-c">Estos cambios afectan el cálculo de asistencia y planilla. Elegí desde cuándo aplican para preservar el histórico anterior.</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-[#FAFAF7]" data-testid="cambio-efectivo-close"><X size={18} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {diffs.map(d => (
            <div key={d.field} className="p-4 rounded-xl border border-c bg-[#FAFAF7]">
              <div className="font-medium text-sm mb-2">{FIELD_LABELS[d.field] || d.field}</div>
              <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
                <span className="np-badge np-badge-neutral">Antes: {formatFieldValue(d.field, d.old_value)}</span>
                <span className="text-secondary-c">→</span>
                <span className="np-badge np-badge-success">Nuevo: {formatFieldValue(d.field, d.new_value)}</span>
              </div>
              <label className="text-xs text-secondary-c flex items-center gap-2">
                <Calendar size={12} /> Aplica desde:
                <input
                  type="date"
                  value={fechas[d.field] || today}
                  onChange={e => setFechas(prev => ({ ...prev, [d.field]: e.target.value }))}
                  className="np-input py-1 px-2 text-xs"
                  data-testid={`cambio-fecha-${d.field}`}
                />
              </label>
            </div>
          ))}
          <div className="text-xs text-secondary-c leading-relaxed p-3 rounded-xl bg-[#FFF4E5] border border-[#F2DFB6]">
            <strong>Importante:</strong> para registros de asistencia o planillas con fecha <strong>anterior</strong> a la fecha efectiva elegida se seguirá usando el valor <strong>previo</strong>. Para fechas iguales o posteriores se usará el nuevo valor.
          </div>
        </div>

        <div className="px-6 py-4 border-t border-c flex items-center justify-end gap-2 bg-[#FAFAF7]">
          <button onClick={onCancel} className="np-btn np-btn-outline" data-testid="cambio-efectivo-cancel">Cancelar</button>
          <button onClick={confirm} className="np-btn np-btn-primary" data-testid="cambio-efectivo-confirm">
            <Check size={14} /> Confirmar y guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
